import { Injectable } from '@angular/core';
import { MainMenuItem, MenuItem } from '../interfaces/menu.interface';

/**
 * Menu items that borrow another menu item's role access until the backend adds them to
 * `page_authorization.menus.allowedMenuIds`. Key = the new menu_id, value = the menu_id it
 * inherits access from.
 *
 * TODO: remove goodToPayTimesheets once it is present in allowedMenuIds.
 */
export const MENU_ACCESS_INHERITANCE: Record<string, string> = {
  goodToPayTimesheets: 'pendedTimesheets',
  goodToPayInvoices: 'pendedTimesheets',
};

/** True when a menu id is allowed directly, or inherits access from one that is. */
export function isMenuIdAllowed(menuId: string, allowedMenuIds: Set<string>): boolean {
  const inheritsFrom = MENU_ACCESS_INHERITANCE[menuId];
  return allowedMenuIds.has(menuId) || (!!inheritsFrom && allowedMenuIds.has(inheritsFrom));
}

export interface MenuTabGroup {
  label: string;
  tabs: { menuId: string; label: string }[];
}

/** A tab the current role can open: its label and the route (landing_component) it links to. */
export interface MenuTabLink {
  label: string;
  route: string;
}

/**
 * Menu items shown as tabs on one page instead of as separate menu entries. Key = the anchor
 * menu_id: it keeps its place in the dropdown (relabelled), and the other tab menu items are hidden
 * from the dropdown. Each tab keeps its own menu_id, route and AuthGuard, so access is unchanged.
 */
export const MENU_TAB_GROUPS: Record<string, MenuTabGroup> = {
  timesheets: {
    label: 'Timesheets',
    tabs: [
      { menuId: 'timesheets', label: 'All' },
      { menuId: 'pendedTimesheets', label: 'Pended' },
      { menuId: 'goodToPayTimesheets', label: 'Good To Pay' },
      { menuId: 'payrollAdjustment', label: 'Adjustments' },
      { menuId: 'payrollApprovals', label: 'Approvals' },
    ],
  },
  // Corrections act on timesheet and invoice lines alike, so they get their own group.
  revertAdjustment: {
    label: 'Payroll Corrections',
    tabs: [
      { menuId: 'revertAdjustment', label: 'Revert Adjustments' },
      { menuId: 'chequeVoid', label: 'Void Checks' },
      { menuId: 'navReset', label: 'NAV Reset' },
      { menuId: 'reprocess', label: 'Reprocess' },
    ],
  },
  invoices: {
    label: 'Invoices',
    tabs: [
      { menuId: 'invoices', label: 'All' },
      { menuId: 'pendedInvoices', label: 'Pended' },
      { menuId: 'goodToPayInvoices', label: 'Good To Pay' },
      { menuId: 'invoiceAdjustments', label: 'Adjustments' },
      { menuId: 'invoiceApprovals', label: 'Approvals' },
    ],
  },
  purchaseRequests: {
    label: 'Purchase Requests',
    tabs: [
      { menuId: 'purchaseRequests', label: 'All' },
      { menuId: 'purchaseAdjustments', label: 'Adjustments' },
      { menuId: 'purchaseRequestApprovals', label: 'Approvals' },
    ],
  },
};

const TAB_MENU_IDS = new Set(
  Object.values(MENU_TAB_GROUPS).flatMap(group => group.tabs.map(tab => tab.menuId))
);

@Injectable({
  providedIn: 'root'
})
export class MenuService {
  getMenu(code: string): MainMenuItem {
    return require(`../../assets/data/programs/${code}/main_menu.json`);
  }

  /** The first section of the program's menu; a role lands on its first allowed item after login. */
  getLandingCategory(programCode: string): string {
    return Object.keys(this.getMenu(programCode))[0];
  }

  /**
   * Allow-list filtering: include menu items whose menu_id is in allowedMenuIds, or that inherit
   * access from one that is (see MENU_ACCESS_INHERITANCE). Each MENU_TAB_GROUPS group collapses to
   * one entry at its anchor's position, shown when any of its tabs is allowed and landing on the
   * first allowed tab.
   */
  getAllowedMenus(programCode: string, allowedMenuIds: string[]): MainMenuItem {
    const menu = this.getMenu(programCode);
    const allowed = new Set(allowedMenuIds);
    return Object.keys(menu).reduce((acc, category) => {
      const items = menu[category].flatMap((item): MenuItem[] => {
        if (item.display !== 'y') return [];
        const group = MENU_TAB_GROUPS[item.menu_id];
        if (group) {
          const tabs = this.getAllowedTabs(programCode, allowedMenuIds, item.menu_id);
          return tabs.length > 0
            ? [{ ...item, menu_label: group.label, landing_component: tabs[0].route }]
            : [];
        }
        if (TAB_MENU_IDS.has(item.menu_id)) return [];
        return isMenuIdAllowed(item.menu_id, allowed) ? [item] : [];
      });
      if (items.length > 0) {
        acc[category] = items;
      }
      return acc;
    }, {} as MainMenuItem);
  }

  /** Tabs of a MENU_TAB_GROUPS group that are displayed in this program's menu and allowed for the role. */
  getAllowedTabs(programCode: string, allowedMenuIds: string[], groupId: string): MenuTabLink[] {
    const group = MENU_TAB_GROUPS[groupId];
    if (!group) return [];
    const allowed = new Set(allowedMenuIds);
    const menuItems = new Map(
      Object.values(this.getMenu(programCode)).flat()
        .filter(item => item.display === 'y')
        .map(item => [item.menu_id, item])
    );
    return group.tabs.flatMap(tab => {
      const route = menuItems.get(tab.menuId)?.landing_component;
      return route && isMenuIdAllowed(tab.menuId, allowed) ? [{ label: tab.label, route }] : [];
    });
  }

  /** Id of the MENU_TAB_GROUPS group that has a tab routed to `route` in this program's menu. */
  findTabGroupId(programCode: string, route: string): string | undefined {
    const menuIdsForRoute = new Set(
      Object.values(this.getMenu(programCode)).flat()
        .filter(item => item.landing_component === route)
        .map(item => item.menu_id)
    );
    return Object.keys(MENU_TAB_GROUPS).find(groupId =>
      MENU_TAB_GROUPS[groupId].tabs.some(tab => menuIdsForRoute.has(tab.menuId))
    );
  }

  getAvailableMenus(programCode: string, restrictedMenus: string[]) {
    const menu = this.getMenu(programCode);
    return this.filterMenuItems(menu, restrictedMenus);
  }

  filterMenuItems = (menu: MainMenuItem, restrictedMenus: string[]) => {
    // Iterate through the categories in the menu and filter the items within each category
    const filteredMenuItems = Object.keys(menu).reduce((acc, category) => {
      // Filter the menu items for the current category
      const categoryItems = menu[category].filter((menuItem) => {
        // Check if the menu item is visible (display === "y")
        if (menuItem.display !== "y") return false;
  
        // Check if the menu item is in the restricted menus
        if (restrictedMenus.includes(menuItem.menu_id)) return false;
  
        // If both checks pass, include the menu item
        return true;
      });
  
      // Only include categories that have valid menu items
      if (categoryItems.length > 0) {
        acc[category] = categoryItems;
      }
  
      return acc;
    }, {} as MainMenuItem); // Initialize the accumulator as MainMenuItem
  
    return filteredMenuItems;
  };
}